import React, { useState } from 'react';
import { Drawer, Steps, Button, Upload, message, Select, Radio, Alert, Flex, Typography, Space, Result, Divider, Tag, Modal } from 'antd';
import Table from 'components/Common/Table';
import { InboxOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import * as actions from "store/actions/AuthActions";
import useIsMobile from 'hooks/useIsMobile';

const { Dragger } = Upload;
const { Text, Title, Link } = Typography;
const { Step } = Steps;
const { confirm } = Modal;

const ImportContactsDrawer = ({ visible, onClose, campaignId, onSuccess }) => {
    const isMobile = useIsMobile();
    const [currentStep, setCurrentStep] = useState(0);
    const [fileList, setFileList] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [fieldMapping, setFieldMapping] = useState({});
    const [duplicateMode, setDuplicateMode] = useState('add_new');
    const [importResults, setImportResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterType, setFilterType] = useState('all'); // 'all', 'mapped', 'unmapped'
    const [fileUploaded, setFileUploaded] = useState(false);

    // Reset state when drawer closes
    const handleClose = () => {
        setCurrentStep(0);
        setFileList([]);
        setUploadedFile(null);
        setParsedData(null);
        setFieldMapping({});
        setDuplicateMode('add_new');
        setImportResults(null);
        setLoading(false);
        setFilterType('all');
        setFileUploaded(false);
        onClose();
    };

    // Step 1: Upload file
    const uploadProps = {
        name: 'file',
        multiple: false,
        fileList,
        accept: '.csv,.xls,.xlsx',
        beforeUpload: (file) => {
            const isValid = file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx');
            if (!isValid) {
                message.error('Please upload CSV or Excel file only');
                return false;
            }
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error('File must be smaller than 10MB');
                return false;
            }
            setFileList([file]);
            setUploadedFile(file);
            setFileUploaded(true);
            return false; // Prevent auto upload
        },
        onRemove: () => {
            setFileList([]);
            setUploadedFile(null);
            setFileUploaded(false);
        },
    };

    const handleDownloadTemplate = async (format) => {
        try {
            const response = await actions.downloadContactImportTemplate(format);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `contact_import_template.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success(`Template downloaded successfully`);
        } catch (error) {
            message.error('Failed to download template');
        }
    };

    // Handle next from Step 1 (upload) to Step 2 (mapping)
    const handleParseFile = async () => {
        if (!uploadedFile) {
            message.error('Please upload a file first');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);

            const response = await actions.parseContactImportFile(campaignId, formData);

            setParsedData(response.data);

            // Apply auto-mapping
            if (response.data.auto_mapping) {
                setFieldMapping(response.data.auto_mapping);
            }

            setCurrentStep(1);
            message.success('File parsed successfully');
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to parse file');
        }
        setLoading(false);
    };

    // Apply auto-mapping
    const handleAutoMapping = () => {
        if (parsedData?.auto_mapping) {
            setFieldMapping(parsedData.auto_mapping);
            message.success('Auto-mapping applied');
        }
    };

    // Reset field mapping
    const handleResetMapping = () => {
        setFieldMapping({});
        message.info('Field mapping reset');
    };

    // Handle field mapping change
    const handleMappingChange = (fileHeader, crmField) => {
        // If clearing the mapping (selecting empty option)
        if (!crmField || crmField === '') {
            setFieldMapping(prev => ({
                ...prev,
                [fileHeader]: crmField
            }));
            return;
        }

        // Check if this CRM field is already mapped to another file column
        const alreadyMappedTo = Object.keys(fieldMapping).find(
            key => key !== fileHeader && fieldMapping[key] === crmField
        );

        if (alreadyMappedTo) {
            // Get the CRM field label
            const crmFieldLabel = parsedData.crm_fields.find(f => f.value === crmField)?.label || crmField;

            // Show confirmation dialog
            confirm({
                title: 'Override Existing Mapping?',
                icon: <ExclamationCircleOutlined />,
                content: (
                    <div>
                        <Text>
                            <strong>{crmFieldLabel}</strong> has been mapped with <strong>'{alreadyMappedTo}'</strong>.
                        </Text>
                        <br />
                        <Text>
                            By mapping it with <strong>'{fileHeader}'</strong>, the previous mapping will be removed.
                        </Text>
                    </div>
                ),
                okText: 'Override',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk() {
                    // Clear the old mapping and set the new one
                    setFieldMapping(prev => ({
                        ...prev,
                        [alreadyMappedTo]: '',  // Clear old mapping
                        [fileHeader]: crmField  // Set new mapping
                    }));
                    message.success('Field mapping updated');
                },
                onCancel() {
                    // Do nothing - keep the old mapping
                },
            });
        } else {
            // No conflict, just update the mapping
            setFieldMapping(prev => ({
                ...prev,
                [fileHeader]: crmField
            }));
        }
    };

    // Count mapped and unmapped fields
    const getMappingStats = () => {
        if (!parsedData) return { mapped: 0, unmapped: 0 };
        const mapped = Object.values(fieldMapping).filter(val => val && val !== '').length;
        const unmapped = parsedData.file_headers.length - mapped;
        return { mapped, unmapped };
    };

    // Validate mapping before proceeding
    const validateMapping = () => {
        const hasFirstName = Object.values(fieldMapping).includes('first_name');
        if (!hasFirstName) {
            message.error('Please map at least one column to First Name (required field)');
            return false;
        }
        return true;
    };

    // Handle import
    const handleImport = async () => {
        setLoading(true);
        try {
            const response = await actions.importCampaignContacts(campaignId, {
                file_id: parsedData.file_id,
                field_mapping: fieldMapping,
                duplicate_mode: duplicateMode
            });

            setImportResults(response.data);
            setCurrentStep(3);
        } catch (error) {
            message.error(error.response?.data?.error || 'Import failed');
        }
        setLoading(false);
    };

    // Finish and close
    const handleFinish = () => {
        if (onSuccess) {
            onSuccess();
        }
        handleClose();
    };

    // Step navigation
    const handleNext = () => {
        if (currentStep === 0) {
            handleParseFile();
        } else if (currentStep === 1) {
            if (validateMapping()) {
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            handleImport();
        }
    };

    const handlePrevious = () => {
        setCurrentStep(prev => prev - 1);
    };

    const getStepTitle = () => {
        if (currentStep === 0) {
            return 'Import Contacts';
        } else if (currentStep === 1) {
            return `Map Import Fields to CRM - ${uploadedFile?.name || ''}`;
        } else if (currentStep === 2) {
            return 'Configure Import';
        } else if (currentStep === 3) {
            return 'Import Results';
        }
        return 'Import Contacts';
    };

    // Render Step 1: Upload
    const renderUploadStep = () => (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ maxWidth: 600, margin: '0 auto', border: '1px solid #f0f0f0', borderRadius: 8, padding: 40 }}>
                <div style={{ marginBottom: 30 }}>
                    <InboxOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                    <Title level={4}>From File</Title>
                </div>

                {fileUploaded ? (
                    <div style={{ marginBottom: 20, padding: 30, border: '1px solid #d9f7be', borderRadius: 8, backgroundColor: '#f6ffed' }}>
                        <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                        <div>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>Your file has been uploaded successfully.</Text>
                            <Text type="secondary">{uploadedFile?.name}</Text>
                        </div>
                    </div>
                ) : (
                    <Dragger {...uploadProps} style={{ marginBottom: 20 }}>
                        <p className="ant-upload-text">Drag and drop your file here.</p>
                        <p className="ant-upload-hint">- or -</p>
                        <Button type="primary" style={{ marginTop: 8 }}>Browse</Button>
                    </Dragger>
                )}

                <div style={{ marginBottom: 30 }}>
                    <Text type="secondary">Download sample file </Text>
                    <Link onClick={() => handleDownloadTemplate('csv')}>CSV</Link>
                    <Text type="secondary"> or </Text>
                    <Link onClick={() => handleDownloadTemplate('xlsx')}>XLSX</Link>
                </div>

                <Text type="secondary" style={{ fontSize: 12 }}>
                    You can import up to 1000 records through an .xls, .xlsx, .vcf or .csv file.
                    To import more than 1000 records at a time, use a .csv file.
                </Text>
            </div>
        </div>
    );

    // Render Step 2: Field Mapping
    const renderMappingStep = () => {
        if (!parsedData) return null;

        const stats = getMappingStats();

        const columns = [
            {
                title: 'FIELDS IN FILE',
                dataIndex: 'fileHeader',
                key: 'fileHeader',
                width: '30%',
            },
            {
                title: 'FIELDS IN CRM',
                dataIndex: 'crmField',
                key: 'crmField',
                width: '30%',
                render: (_, record) => {
                    // Get list of already assigned CRM fields
                    const assignedFields = new Set(
                        Object.entries(fieldMapping)
                            .filter(([key, value]) => key !== record.fileHeader && value && value !== '')
                            .map(([, value]) => value)
                    );

                    // Separate fields into assigned and unassigned
                    const unassignedFields = parsedData.crm_fields.filter(
                        field => !assignedFields.has(field.value) || field.value === ''
                    );
                    const assignedFieldsList = parsedData.crm_fields.filter(
                        field => assignedFields.has(field.value)
                    );

                    return (
                        <Select
                            style={{ width: '100%' }}
                            value={fieldMapping[record.fileHeader] || ''}
                            onChange={(value) => handleMappingChange(record.fileHeader, value)}
                            placeholder="Select Field To Import"
                        >
                            {/* Unassigned Fields */}
                            <Select.OptGroup label="UNASSIGNED">
                                {unassignedFields.map(field => (
                                    <Select.Option key={field.value} value={field.value}>
                                        {field.label} {field.required ? '*' : ''}
                                    </Select.Option>
                                ))}
                            </Select.OptGroup>

                            {/* Assigned Fields (if any) */}
                            {assignedFieldsList.length > 0 && (
                                <Select.OptGroup label="ASSIGNED">
                                    {assignedFieldsList.map(field => (
                                        <Select.Option key={field.value} value={field.value}>
                                            {field.label} {field.required ? '*' : ''}
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            )}
                        </Select>
                    );
                },
            },
            {
                title: 'SAMPLE DATA FROM FILE',
                dataIndex: 'sampleData',
                key: 'sampleData',
                width: '40%',
                render: (_, record) => {
                    const headerIndex = parsedData.file_headers.indexOf(record.fileHeader);
                    const samples = parsedData.sample_data.map(row => row[headerIndex]).filter(val => val);
                    return (
                        <Space split={<Divider type="vertical" />} size="small">
                            {samples.slice(0, 2).map((sample, idx) => (
                                <Text key={idx} style={{ fontSize: 13, color: '#666', display: 'inline-block', maxWidth: 150 }} ellipsis={{ tooltip: sample }}>
                                    {sample}
                                </Text>
                            ))}
                        </Space>
                    );
                },
            },
        ];

        // Filter data based on filter type
        let filteredHeaders = parsedData.file_headers;
        if (filterType === 'mapped') {
            filteredHeaders = parsedData.file_headers.filter(header =>
                fieldMapping[header] && fieldMapping[header] !== ''
            );
        } else if (filterType === 'unmapped') {
            filteredHeaders = parsedData.file_headers.filter(header =>
                !fieldMapping[header] || fieldMapping[header] === ''
            );
        }

        const dataSource = filteredHeaders.map(header => ({
            key: header,
            fileHeader: header,
        }));

        return (
            <div style={{ padding: 20 }}>
                <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                    <Space>
                        <Tag
                            style={{ cursor: 'pointer', fontSize: 14, padding: '4px 12px' }}
                            color={filterType === 'all' ? 'blue' : 'default'}
                            onClick={() => setFilterType('all')}
                        >
                            All ({parsedData.file_headers.length})
                        </Tag>
                        <Tag
                            style={{ cursor: 'pointer', fontSize: 14, padding: '4px 12px' }}
                            color={filterType === 'mapped' ? 'blue' : 'default'}
                            onClick={() => setFilterType('mapped')}
                        >
                            Mapped ({stats.mapped})
                        </Tag>
                        <Tag
                            style={{ cursor: 'pointer', fontSize: 14, padding: '4px 12px' }}
                            color={filterType === 'unmapped' ? 'blue' : 'default'}
                            onClick={() => setFilterType('unmapped')}
                        >
                            Unmapped ({stats.unmapped})
                        </Tag>
                    </Space>
                    <Space>
                        <Button onClick={handleResetMapping}>Reset Field Mapping</Button>
                        <Button type="primary" onClick={handleAutoMapping}>Apply Auto Mapping</Button>
                    </Space>
                </Flex>

                <Alert
                    message={`Total records to import: ${parsedData.total_records}`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    size="small"
                    scroll={{ y: 400 }}
                />
            </div>
        );
    };

    // Render Step 3: Configure
    const renderConfigureStep = () => (
        <div style={{ padding: 40 }}>
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
                <Title level={4}>Duplicate Handling</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                    Choose how to handle contacts with duplicate email addresses:
                </Text>

                <Radio.Group value={duplicateMode} onChange={(e) => setDuplicateMode(e.target.value)} style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Radio value="add_new" style={{ marginBottom: 16 }}>
                            <div>
                                <Text strong>Add as new contact</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Always create new contacts, even if email already exists
                                </Text>
                            </div>
                        </Radio>

                        <Radio value="update_existing" style={{ marginBottom: 16 }}>
                            <div>
                                <Text strong>Update existing fields only</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Update contacts with matching email, skip if no match found
                                </Text>
                            </div>
                        </Radio>

                        <Radio value="both">
                            <div>
                                <Text strong>Both (recommended)</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Update if email exists, create new if it doesn't
                                </Text>
                            </div>
                        </Radio>
                    </Space>
                </Radio.Group>

                {parsedData && (
                    <Alert
                        message={`Ready to import ${parsedData.total_records} records`}
                        type="success"
                        showIcon
                        style={{ marginTop: 30 }}
                    />
                )}
            </div>
        </div>
    );

    // Render Step 4: Results
    const renderResultsStep = () => {
        if (!importResults) return null;

        const hasErrors = importResults.failed_count > 0;
        const totalProcessed = importResults.success_count + importResults.updated_count + importResults.failed_count;

        return (
            <div style={{ padding: 40 }}>
                <Result
                    status={hasErrors ? 'warning' : 'success'}
                    title={hasErrors ? 'Import Completed with Errors' : 'Import Completed Successfully'}
                    subTitle={`Processed ${totalProcessed} records from your file`}
                    extra={[
                        <Button type="primary" key="done" onClick={handleFinish}>
                            Done
                        </Button>,
                    ]}
                />

                <Divider />

                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        {importResults.success_count > 0 && (
                            <Alert
                                message={`Successfully created ${importResults.success_count} new contacts`}
                                type="success"
                                showIcon
                                icon={<CheckCircleOutlined />}
                            />
                        )}

                        {importResults.updated_count > 0 && (
                            <Alert
                                message={`Updated ${importResults.updated_count} existing contacts`}
                                type="info"
                                showIcon
                                icon={<CheckCircleOutlined />}
                            />
                        )}

                        {importResults.failed_count > 0 && (
                            <Alert
                                message={`Failed to import ${importResults.failed_count} records`}
                                type="error"
                                showIcon
                                icon={<CloseCircleOutlined />}
                                description={
                                    importResults.errors && importResults.errors.length > 0 && (
                                        <div style={{ marginTop: 16 }}>
                                            <Text strong>Error Details:</Text>
                                            <Table
                                                size="small"
                                                dataSource={importResults.errors.map((err, idx) => ({
                                                    key: idx,
                                                    row: err.row,
                                                    error: err.error
                                                }))}
                                                columns={[
                                                    {
                                                        title: 'Row',
                                                        dataIndex: 'row',
                                                        key: 'row',
                                                        width: 80,
                                                    },
                                                    {
                                                        title: 'Error',
                                                        dataIndex: 'error',
                                                        key: 'error',
                                                    },
                                                ]}
                                                pagination={{ defaultPageSize: 5, showSizeChanger: true }}
                                                scroll={{ y: 200 }}
                                            />
                                        </div>
                                    )
                                }
                            />
                        )}
                    </Space>
                </div>
            </div>
        );
    };

    return (
        <Drawer
            title={getStepTitle()}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 800}
            height={isMobile ? "95vh" : undefined}
            open={visible}
            onClose={handleClose}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 8 }}>
                    <Button onClick={handleClose} style={isMobile ? { flex: 1 } : undefined}>
                        Cancel
                    </Button>
                    {currentStep > 0 && currentStep < 3 && (
                        <Button onClick={handlePrevious} style={isMobile ? { flex: 1 } : undefined}>
                            Previous
                        </Button>
                    )}
                    {currentStep < 3 && (
                        <Button
                            type="primary"
                            onClick={handleNext}
                            loading={loading}
                            disabled={currentStep === 0 && !uploadedFile}
                            style={isMobile ? { flex: 1 } : undefined}
                        >
                            {currentStep === 2 ? 'Import' : 'Next'}
                        </Button>
                    )}
                </div>
            }
        >
            <Steps current={currentStep} style={{ marginBottom: 30 }}>
                <Step title="Upload File" />
                <Step title="Map Fields" />
                <Step title="Configure" />
                <Step title="Results" />
            </Steps>

            {currentStep === 0 && renderUploadStep()}
            {currentStep === 1 && renderMappingStep()}
            {currentStep === 2 && renderConfigureStep()}
            {currentStep === 3 && renderResultsStep()}
        </Drawer>
    );
};

export default ImportContactsDrawer;
